import { useState, useEffect } from 'react';
import { format, parseISO, differenceInDays } from 'date-fns';
import { ru } from 'date-fns/locale';
import { useAuthStore } from '@shared/store/authStore';

interface ClubCard {
  card_id: number;
  TypeId: number;
  name: string;
  card_status: {
    id: number;
    name: string;
  };
  date_sale: string;
  date_start: string;
  date_end: string;
  freez_count: number;
  freez_days_count: number;
  freez_count_remainder: number;
  freez_days_count_remainder: number;
  min_freez_days: number;
  services: any[];
}

interface CardType {
  CardTypeId: number;
  name: string;
  note: string;
  price: number;
}

const cardStatusColors: Record<number, string> = {
  1: 'bg-green-100 text-green-800',  // активна
  3: 'bg-gray-100 text-gray-800',    // истекла
  4: 'bg-blue-100 text-blue-800',    // заморожена
  6: 'bg-red-100 text-red-800',      // заблокирована
};

export function ClubCardsPage() {
  const { user, apiClient } = useAuthStore();
  const [activeCards, setActiveCards] = useState<ClubCard[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showFreezeModal, setShowFreezeModal] = useState(false);
  const [selectedCard, setSelectedCard] = useState<ClubCard | null>(null);
  const [freezeDays, setFreezeDays] = useState(3);
  const [autoRenew, setAutoRenew] = useState(true); // По умолчанию включено
  const [showBuyModal, setShowBuyModal] = useState(false);
  const [availableCards, setAvailableCards] = useState<CardType[]>([]);
  const [selectedCardType, setSelectedCardType] = useState<CardType | null>(null);

  useEffect(() => {
    loadCards();
    loadAvailableCards();

    // Check if returning from ЮKassa payment
    checkPendingPayment();
  }, [user, apiClient]);

  const checkPendingPayment = async () => {
    const pendingPaymentId = localStorage.getItem('pending_payment_id');
    if (!pendingPaymentId) return;

    console.log('Checking pending payment:', pendingPaymentId);

    try {
      const response = await fetch(`/api/yookassa/check-payment/${pendingPaymentId}`);
      const result = await response.json();

      if (result.success && result.paid) {
        // Payment successful - card purchased!
        localStorage.removeItem('pending_payment_id');
        await loadCards(); // Reload cards to show new card
      } else if (result.status === 'pending') {
        // Still pending - user might have canceled
        console.log('Payment still pending');
      }
    } catch (error) {
      console.error('Error checking payment:', error);
    }
  };

  const loadCards = async () => {
    if (!user || !apiClient) return;

    setIsLoading(true);
    try {
      const cards = await apiClient.getClientCards(user.id);
      // Фильтруем только активные карты (card_status.id === 1)
      const activeOnly = cards.filter(card => card.card_status.id === 1);
      setActiveCards(activeOnly);
    } catch (error) {
      console.error('Error loading cards:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadAvailableCards = async () => {
    if (!apiClient) return;

    try {
      const cards = await apiClient.getCardTypes();
      setAvailableCards(cards);
    } catch (error) {
      console.error('Error loading available cards:', error);
    }
  };


  const handleFreezeCard = async () => {
    if (!user || !apiClient || !selectedCard) return;

    if (freezeDays < selectedCard.min_freez_days) {
      return;
    }

    if (freezeDays > selectedCard.freez_days_count_remainder) {
      return;
    }

    setIsLoading(true);
    try {
      const freezeDate = format(new Date(), 'yyyy-MM-dd');
      await apiClient.freezeCard(user.id, selectedCard.card_id, freezeDays, freezeDate);
      setShowFreezeModal(false);
      setSelectedCard(null);
      await loadCards();
    } catch (error) {
      console.error('Error freezing card:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBuyCard = async () => {
    if (!user || !selectedCardType) return;

    setIsLoading(true);
    try {
      // Create payment in ЮKassa
      const response = await fetch('/api/yookassa/create-payment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          clientId: user.id,
          cardTypeId: selectedCardType.CardTypeId,
          price: selectedCardType.price,
          phone: user.phone,
          email: user.email || '',
          autoRenew
        })
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to create payment');
      }

      // Save payment ID to check status after return
      localStorage.setItem('pending_payment_id', result.paymentId);

      // Redirect to ЮKassa payment page
      window.location.href = result.confirmationUrl;

    } catch (error) {
      console.error('Error creating payment:', error);
      setIsLoading(false);
    }
  };

  const getDaysLeft = (endDate: string): number => {
    return differenceInDays(parseISO(endDate), new Date());
  };

  return (
    <>
      {/* Main Content - скрывается когда открыто модальное окно */}
      {!showBuyModal && !showFreezeModal && (
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>Клубные карты</h1>
            {isLoading && (
              <div className="w-6 h-6 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
            )}
          </div>

          {/* Auto-renew toggle */}
          <div className="rounded-2xl p-4 shadow-sm" style={{ backgroundColor: 'var(--card-bg)' }}>
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold" style={{ color: 'var(--text-primary)' }}>Автопродление</h3>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  {autoRenew ? 'Карта будет автоматически продлена перед окончанием срока' : 'Вы получите уведомление перед окончанием срока карты'}
                </p>
              </div>
              <button
                onClick={() => setAutoRenew(!autoRenew)}
                style={{
                  backgroundColor: autoRenew ? '#22c55e' : '#9ca3af'
                }}
                className="relative inline-flex h-7 w-12 items-center rounded-full transition-colors"
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform ${
                    autoRenew ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>

          {/* Active Cards */}
          <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Мои карты</h2>
          <button
            onClick={() => setShowBuyModal(true)}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700"
          >
            Купить карту
          </button>
        </div>
        {activeCards.length > 0 ? (
          <div className="space-y-3">
            {activeCards.map((card) => {
              const daysLeft = getDaysLeft(card.date_end);
              const statusColor = cardStatusColors[card.card_status.id] || 'bg-gray-100 text-gray-800';

              return (
                <div key={card.card_id} className="rounded-2xl p-4 shadow-sm" style={{ backgroundColor: 'var(--card-bg)' }}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-semibold" style={{ color: 'var(--text-primary)' }}>{card.name}</h3>
                      <span className={`inline-block px-2 py-1 text-xs rounded-full mt-1 ${statusColor}`}>
                        {card.card_status.name}
                      </span>
                    </div>
                    {card.card_status.id === 1 && daysLeft <= 7 && (
                      <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full">
                        {daysLeft} {daysLeft === 1 ? 'день' : 'дней'}
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-3 mb-3 text-sm">
                    <div>
                      <p style={{ color: 'var(--text-secondary)' }}>Дата начала</p>
                      <p style={{ color: 'var(--text-primary)' }}>{format(parseISO(card.date_start), 'dd MMM yyyy', { locale: ru })}</p>
                    </div>
                    <div>
                      <p style={{ color: 'var(--text-secondary)' }}>Дата окончания</p>
                      <p style={{ color: 'var(--text-primary)' }}>{format(parseISO(card.date_end), 'dd MMM yyyy', { locale: ru })}</p>
                    </div>
                  </div>

                  {card.freez_count_remainder > 0 && (
                    <div className="mb-3 p-2 rounded-lg" style={{ backgroundColor: 'var(--bg-secondary)' }}>
                      <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                        Доступно заморозок: {card.freez_count_remainder} ({card.freez_days_count_remainder} дней)
                      </p>
                    </div>
                  )}

                  {card.card_status.id === 1 && (
                    <button
                      onClick={() => {
                        setSelectedCard(card);
                        setShowFreezeModal(true);
                        setFreezeDays(card.min_freez_days);
                      }}
                      disabled={card.freez_count_remainder === 0}
                      className="w-full py-2 px-4 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Заморозить карту
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12 rounded-2xl" style={{ backgroundColor: 'var(--card-bg)' }}>
            <div className="text-4xl mb-2">💳</div>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>У вас пока нет активных карт</p>
          </div>
        )}
          </div>
        </div>
      )}

      {/* Freeze Modal */}
      {showFreezeModal && selectedCard && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="rounded-2xl p-6 max-w-md w-full" style={{ backgroundColor: 'var(--card-bg)' }}>
            <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>Заморозка карты</h3>
            <p className="mb-4" style={{ color: 'var(--text-primary)' }}>{selectedCard.name}</p>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                Количество дней (мин. {selectedCard.min_freez_days})
              </label>
              <input
                type="number"
                min={selectedCard.min_freez_days}
                max={selectedCard.freez_days_count_remainder}
                value={freezeDays}
                onChange={(e) => setFreezeDays(parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)' }}
              />
              <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
                Доступно: {selectedCard.freez_days_count_remainder} дней
              </p>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowFreezeModal(false);
                  setSelectedCard(null);
                }}
                className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-sm font-medium"
                style={{ color: 'var(--text-primary)' }}
              >
                Отмена
              </button>
              <button
                onClick={handleFreezeCard}
                disabled={isLoading}
                className="flex-1 py-2 px-4 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {isLoading ? 'Заморозка...' : 'Заморозить'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Buy Card Modal */}
      {showBuyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ paddingTop: '80px', paddingBottom: '80px' }}>
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black bg-opacity-80"
            onClick={() => {
              setShowBuyModal(false);
              setSelectedCardType(null);
            }}
          />

          {/* Modal */}
          <div
            className="relative rounded-2xl max-w-2xl w-full flex flex-col shadow-2xl"
            style={{
              backgroundColor: 'var(--card-bg)',
              maxHeight: '100%',
              height: 'auto'
            }}
          >
            {/* Header - фиксированный */}
            <div className="flex-shrink-0 p-6 pb-4 border-b" style={{ borderColor: 'var(--border-color)' }}>
              <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Выберите карту</h3>
            </div>

            {/* Content - скроллируемый с padding снизу для кнопок */}
            <div
              className="overflow-y-auto p-6 pb-24"
              style={{
                flex: '1 1 auto',
                minHeight: 0
              }}
            >
              <div className="space-y-3">
                {availableCards.map((cardType) => {
                  const isSelected = selectedCardType?.CardTypeId === cardType.CardTypeId;
                  return (
                    <div
                      key={cardType.CardTypeId}
                      onClick={() => setSelectedCardType(cardType)}
                      className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        isSelected
                          ? 'border-emerald-600'
                          : 'border-gray-200'
                      }`}
                      style={
                        isSelected
                          ? {
                              backgroundColor: 'rgba(16, 185, 129, 0.15)',
                              borderColor: '#10b981'
                            }
                          : {
                              backgroundColor: 'var(--bg-secondary)',
                              borderColor: 'var(--border-color)'
                            }
                      }
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-semibold" style={{ color: 'var(--text-primary)' }}>{cardType.name}</h4>
                          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{cardType.note}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>
                            {cardType.price.toLocaleString('ru-RU')} ₽
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Footer - абсолютно позиционированный внизу модального окна */}
            <div
              className="absolute bottom-0 left-0 right-0 p-6 pt-4 border-t rounded-b-2xl"
              style={{
                borderColor: 'var(--border-color)',
                backgroundColor: 'var(--card-bg)'
              }}
            >
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowBuyModal(false);
                    setSelectedCardType(null);
                  }}
                  className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-sm font-medium"
                  style={{ color: 'var(--text-primary)' }}
                >
                  Отмена
                </button>
                <button
                  onClick={handleBuyCard}
                  disabled={true}
                  className="flex-1 py-2 px-4 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Временно недоступно - ожидаем ключи ЮКассы"
                >
                  {isLoading ? 'Обработка...' : 'Перейти к оплате'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
